import { useParams } from 'react-router-dom';
import { categories, getMoviesByCategory } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

const categoryLabels: Record<string, string> = {
  all: '全部影视',
  movie: '电影',
  tv: '电视剧',
  variety: '综艺',
  anime: '动漫',
};

const categoryDescriptions: Record<string, string> = {
  all: '浏览番茄影视全部影视资源，包含电影、电视剧、综艺、动漫等全类型高清影视内容。',
  movie: '番茄影视电影频道提供海量高清电影资源，涵盖科幻、动作、喜剧、爱情、悬疑等多种类型，免费在线观看国内外最新大片和经典佳作。',
  tv: '番茄影视电视剧频道每日更新热播剧集，包含国产剧、韩剧、美剧、日剧等，全集高清在线观看，追剧不等待。',
  variety: '番茄影视综艺频道汇聚国内外热门综艺节目，真人秀、脱口秀、竞技挑战、音乐舞蹈，搞笑娱乐一网打尽。',
  anime: '番茄影视动漫频道提供最新最全国产动漫和日本动漫资源，热血、搞笑、恋爱、奇幻等类型动漫免费在线观看。',
};

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const movies = getMoviesByCategory(slug);
  const label = categoryLabels[slug] || '全部影视';
  const description = categoryDescriptions[slug] || '';
  const currentCat = categories.find((c) => c.slug === slug);

  const schemaCollection = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `番茄影视 - ${label}`,
    description: description,
    url: `https://www.fanqieyingshi.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '番茄影视',
      url: 'https://www.fanqieyingshi.com',
    },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: movies.length,
      itemListElement: movies.map((movie, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Movie',
          name: movie.title,
          url: `https://www.fanqieyingshi.com/article/${movie.id}`,
          image: movie.poster,
          dateCreated: movie.releaseDate,
          director: { '@type': 'Person', name: movie.director },
        },
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(schemaCollection)}
      </script>

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: label },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[1280px] mx-auto">
            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">
                {currentCat?.icon && (
                  <i className={`${currentCat.icon} text-primary-500 mr-2`}></i>
                )}
                {label}
              </h1>
              <p className="text-sm text-foreground-500 mt-2 max-w-2xl">{description}</p>
            </div>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.label}
                </Link>
              ))}
            </div>

            {movies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {movies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500">暂无该分类的影视内容</p>
              </div>
            )}

            {movies.length > 0 && (
              <div className="mt-8 text-center">
                <p className="text-sm text-foreground-500">
                  共找到 <strong className="text-primary-600">{movies.length}</strong> 部{label}内容
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}